/* eslint-disable react/jsx-props-no-spreading */

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import { Button } from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import FormControlSelect from '../../../../UI/FormComponent/FormControlSelect';
import FormControlTextInput from '../../../../UI/FormComponent/FormControlTextInput';
import FormControlDatePicker from '../../../../UI/FormComponent/FormControlDatePicker';
import { Col, FormRow } from '../../../../UI/Grid';
import { Modal } from '../../../../UI/Modal';
import { sendNotification } from '../../../../UI/Notification';
import {
  ADD_UPDATE_ZKEY_MAPPING,
  MESSAGES,
  DEAL_UNIT_ENUM,
} from '../../../../constants/BeachSwaps';
import useGetZkeyInformation from '../../../../hooks/beachSwaps/useGetZkeyInformation';
import { getCounterpartyData } from '../../../../store/reducers/Counterparty';
import { getSubTerminalData, getTerminalData } from '../../../../store/reducers/Locations';
import { post } from '../../../../services/GenesisApi';
import { IZkeyMapping } from '../../../../types/BeachSwaps.type';
import { ModalFooter } from '../style';
import { IFullState } from '../../../../types/All.type';
import { isNullOrEmptyObject, handleError } from '../../../../utils/common/utils';
import { COMMON_MESSAGES } from '../../../../constants/Common';
import { AppDispatch } from '../../../../store';

const FormProviderWrapper = styled.div`
  .invalid {
    border: 1px solid rgba(217, 54, 56, 1);
  }

  .already-exists {
    color: rgba(217, 54, 56, 1);
    font-style: italic;
    text-align: center;
    font-size: 15px;
    font-weight: 500;
  }

  .shell-date-picker-time-panel-column:last-child {
    display: none;
  }

  .zkey-wrapper {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`;

interface IAddEditTerminalMapping {
  open: boolean;
  onClose: () => void;
  selectedItem: IZkeyMapping | null;
  refreshData: () => void;
  editType: 'add' | 'edit' | null;
}

const AddEditZKeyMapping: React.FC<IAddEditTerminalMapping> = ({
  open = false,
  onClose = () => {},
  selectedItem,
  refreshData = () => {},
  editType,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const methods = useForm();
  const {
    reset,
    watch,
    setValue,
    formState: { isDirty },
  } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [alreadyExists, setAlreadyExists] = useState<{
    error: boolean;
    message: string;
  }>({
    error: false,
    message: '',
  });

  // Watch fields needed for derived/filtered behaviour
  const dealLegIdValue = watch('deal_DealLegId');
  const selectedTerminalId = watch('terminalId');
  const validFromValue = watch('validFrom');
  const validToValue = watch('validTo');

  const { counterpartyData, isLoading: loadingCounterparties } = useSelector(
    (state: IFullState) => state.Counterparty,
  );
  const {
    subTerminalData: { data: subTerminalData },
    terminalData: { data: terminalData },
    isLoading: locationsLoading,
  } = useSelector((state: IFullState) => state.Locations);

  const { loadingZkeyInformation, zkeyInformation } = useGetZkeyInformation(
    selectedItem?.id ?? null,
    editType,
  );

  useEffect(() => {
    dispatch(getCounterpartyData());
    dispatch(getSubTerminalData());
    dispatch(getTerminalData());
  }, [dispatch]);

  useEffect(() => {
    if (
      !loadingZkeyInformation &&
      open &&
      editType === 'edit' &&
      !isNullOrEmptyObject(zkeyInformation)
    ) {
      reset({
        swapType: zkeyInformation?.swapType,
        deal_DealLegId: zkeyInformation?.deal_DealLegId,
        nbp_Deal_DealLegId: zkeyInformation?.nbp_Deal_DealLegId,
        dealUnit: zkeyInformation?.dealUnit,
        counterPartyId: zkeyInformation?.counterPartyId,
        terminalId: zkeyInformation?.terminalId,
        subTerminalId: zkeyInformation?.subTerminalId,
        buySell: zkeyInformation?.buySell === 'SELL' ? 2 : 1,
        primarySecondary: zkeyInformation?.primarySecondary,
        validFrom: zkeyInformation?.validFrom ? dayjs(zkeyInformation.validFrom) : null,
        validTo: zkeyInformation?.validTo ? dayjs(zkeyInformation.validTo) : null,
      });
    } else {
      reset({
        swapType: null,
        deal_DealLegId: '',
        dealUnit: 'Therms',
        nbp_Deal_DealLegId: '',
        counterPartyId: null,
        terminalId: null,
        subTerminalId: null,
        buySell: null,
        primarySecondary: null,
        validFrom: null,
        validTo: null,
      });
    }
  }, [loadingZkeyInformation, editType, zkeyInformation]);

  const handleOnClose = () => {
    setAlreadyExists({
      error: false,
      message: '',
    });
    reset({
      swapType: null,
      deal_DealLegId: '',
      dealUnit: 'Therms',
      nbp_Deal_DealLegId: '',
      counterPartyId: null,
      terminalId: null,
      subTerminalId: null,
      buySell: null,
      primarySecondary: null,
      validFrom: null,
      validTo: null,
    });
    onClose();
  };

  const handleOnSubmit = (formData) => {
    setLoading(true);
    let payload = formData;
    if (selectedItem) {
      payload = {
        ...payload,
        id: selectedItem?.id,
        isActive: true,
        buySell: payload.buySell === 1 ? 'BUY' : 'SELL',
      };
    } else {
      payload = {
        ...payload,
        id: 0,
        isActive: true,
        buySell: payload.buySell === 1 ? 'BUY' : 'SELL',
      };
    }

    post(ADD_UPDATE_ZKEY_MAPPING, {
      ...payload,
      validFrom: dayjs(payload.validFrom).format('YYYY-MM-DD'),
      validTo: dayjs(payload.validTo).format('YYYY-MM-DD'),
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: `Mapping ${zkeyInformation?.id ? 'Updated' : 'Created'} Successfully!`,
        });
        handleOnClose();
        refreshData();
      })
      .catch((error) => {
        handleError(error, COMMON_MESSAGES.ERROR);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <ModalFooter>
      <Button
        onClick={methods.handleSubmit(handleOnSubmit)}
        loading={loading}
        disabled={loading || !isDirty}
      >
        {selectedItem ? 'Update' : 'Save'}
      </Button>
      <Button variant='outlined' onClick={handleOnClose}>
        Cancel
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      title={`${selectedItem ? 'Edit' : 'Add New'} Mapping`}
      isDraggable
      maskClosable={false}
      footer={renderFooter()}
      width='50%'
    >
      <FormProviderWrapper>
        <FormProvider {...methods}>
          <FormRow>
            <Col size={4}>
              <FormControlTextInput
                controllerProps={{
                  name: 'deal_DealLegId',
                  rules: {
                    required: MESSAGES.REQUIRED_DEAL_LEG_ID,
                    maxLength: {
                      value: 50,
                      message: COMMON_MESSAGES.MAX_LENGTH_50,
                    },
                    pattern: {
                      value: /^\d+_\d+$/,
                      message: MESSAGES.INVALID_DEAL_LEG_ID,
                    },
                  },
                }}
                label='Deal ID'
                data-testid='deal_DealLegId'
                methods={methods}
              />
            </Col>
            <Col size={4}>
              <FormControlTextInput
                controllerProps={{
                  name: 'nbp_Deal_DealLegId',
                  rules: {
                    required: MESSAGES.REQUIRED_NBP_DEAL_LEG_ID,
                    maxLength: {
                      value: 50,
                      message: COMMON_MESSAGES.MAX_LENGTH_50,
                    },
                    pattern: {
                      value: /^\d+_\d+$/,
                      message: MESSAGES.INVALID_NBP_DEAL_LEG_ID,
                    },
                    validate: (value) => value !== dealLegIdValue || MESSAGES.NBP_SAME_AS_DEAL,
                  },
                }}
                label='NBP Deal ID'
                data-testid='nbp_Deal_DealLegId'
                methods={methods}
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'dealUnit',
                  rules: {
                    required: MESSAGES.REQUIRED_DEAL_UNIT,
                  },
                }}
                label='Deal Unit'
                isLoading={false}
                data={DEAL_UNIT_ENUM}
                methods={methods}
                data-testid='dealUnit'
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'swapType',
                  rules: {
                    required: MESSAGES.REQUIRED_SWAP_TYPE,
                  },
                }}
                label='Swap Type'
                isLoading={false}
                data={[
                  { id: 'Beach swap', name: 'Beach swap' },
                  {
                    id: 'Capacity Utilisation swap',
                    name: 'Capacity Utilisation swap',
                  },
                ]}
                methods={methods}
                data-testid='swapType'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'counterPartyId',
                  rules: {
                    required: MESSAGES.REQUIRED_COUNTER_PARTY,
                  },
                }}
                label='Counterparty'
                isLoading={loadingCounterparties}
                data={counterpartyData.map((cp) => ({
                  id: cp.id,
                  name: cp.counterpartyName,
                }))}
                methods={methods}
                data-testid='counterPartyId'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'terminalId',
                  rules: {
                    required: MESSAGES.REQUIRED_TERMINAL,
                  },
                }}
                label='Terminal'
                isLoading={locationsLoading}
                data={terminalData.map((term) => ({
                  id: term.id,
                  name: term.terminalName,
                }))}
                handleOnChange={() => setValue('subTerminalId', null, { shouldDirty: true })}
                methods={methods}
                data-testid='terminalId'
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'subTerminalId',
                  rules: {
                    required: MESSAGES.REQUIRED_SUB_TERMINAL,
                  },
                }}
                label='Sub Terminal'
                isLoading={locationsLoading}
                data={subTerminalData
                  .filter(
                    (_) =>
                      !!_.subTerminalName &&
                      (!selectedTerminalId || _.terminalId === selectedTerminalId),
                  )
                  .map((item) => ({
                    name: item.subTerminalName,
                    id: item.subTerminalId,
                  }))}
                methods={methods}
                data-testid='subTerminalId'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'buySell',
                  rules: {
                    required: MESSAGES.REQUIRED_BUY_SELL,
                  },
                }}
                label='Buy/Sell'
                isLoading={false}
                data={[
                  { id: 1, name: 'BUY' },
                  { id: 2, name: 'SELL' },
                ]}
                methods={methods}
                data-testid='buysellfield'
              />
            </Col>
            <Col size={4}>
              <FormControlSelect
                controllerProps={{
                  name: 'primarySecondary',
                  rules: {
                    required: MESSAGES.REQUIRED_PRIMARY_SECONDARY,
                  },
                }}
                label='Primary/Secondary'
                isLoading={false}
                data={[
                  { id: 1, name: 'Primary' },
                  { id: 2, name: 'Secondary' },
                ]}
                methods={methods}
                data-testid='primarySecondary'
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={4}>
              <FormControlDatePicker
                controllerProps={{
                  name: 'validFrom',
                  rules: {
                    required: MESSAGES.REQUIRED_VALID_FROM,
                    validate: (value) => {
                      if (!value || !validToValue) {
                        return true;
                      }
                      return (
                        !dayjs(value).isAfter(dayjs(validToValue), 'day') ||
                        MESSAGES.INVALID_VALIDITY_RANGE
                      );
                    },
                  },
                }}
                label='Valid From'
                methods={methods}
                data-testid='validFrom'
                inputProps={{
                  placeholder: 'DD MM YYYY',
                  disabledDate: (current) =>
                    validToValue ? current.isAfter(validToValue, 'day') : false,
                }}
              />
            </Col>
            <Col size={4}>
              <FormControlDatePicker
                controllerProps={{
                  name: 'validTo',
                  rules: {
                    required: MESSAGES.REQUIRED_VALID_TO,
                    validate: (value) => {
                      if (!value || !validFromValue) {
                        return true;
                      }
                      return (
                        !dayjs(value).isBefore(dayjs(validFromValue), 'day') ||
                        MESSAGES.INVALID_VALIDITY_RANGE
                      );
                    },
                  },
                }}
                label='Valid To'
                methods={methods}
                data-testid='validTo'
                inputProps={{
                  placeholder: 'DD MM YYYY',
                  disabled: !validFromValue,
                  disabledDate: (current) =>
                    validFromValue ? current.isBefore(validFromValue, 'day') : false,
                }}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col size={12}>
              {alreadyExists.error && <div className='already-exists'>{alreadyExists.message}</div>}
            </Col>
          </FormRow>
        </FormProvider>
      </FormProviderWrapper>
    </Modal>
  );
};

export default AddEditZKeyMapping;
